/**
 * evaluator.ts —— 7 张取 5 的高性能牌力评估器（PRD 4.2 Step 1、AC1.5）
 *
 * ============================ 设计取舍 ============================
 * 方案备选与结论：
 *   A. 21 组合硬算（C(7,5)=21 次 5 张评估）—— 正确但慢 21 倍，赢率热路径不可接受。
 *   B. two-plus-two 32 位状态机查表 —— 最快，但表体 ~124MB，Web 端包体不可接受
 *      （PRD 十一章已列为风险：「查表式评估器约 32MB，影响包体」）。
 *   C. 本实现：4 个 13 位花色位图 + 纯位运算全加器 + 4 张 8192 项小表。
 *      表总计 4×8192×4B ≈ 128KB，可完整驻留 L2；单次评估约 20~40 条整数指令，
 *      无分支预测灾难、无内存墙。**选 C**，并在 scripts/build-preflop-table/gen_preflop.c
 *      里做了完全同构的 C 实现，保证离线表与运行时口径逐位一致。
 *
 * ============================ 牌力值格式 ============================
 *   value = (category << 20) | (r1 << 16) | (r2 << 12) | (r3 << 8) | (r4 << 4) | r5
 *   category 1..9（高牌..同花顺），r* 为按重要性降序排列的关键点数。
 *   两手牌直接比较整数即可判定胜/负/平 —— 这是「同牌型踢脚牌比较」正确的前提，
 *   也是 PRD 4.2 强调「不能只用 9 种牌型做粒度」的落地方式。
 *   值域上界 (9<<20)|0xFFFFF = 10,485,759，安全落在 32 位有符号整数内。
 */

import { NUM_RANKS, RANK_LETTERS, RANK_NAMES_CN, rankOf, suitOf, type Card } from './cards';

// ============================ 牌型分类 ============================

export const enum HandCategory {
  HighCard = 1,
  Pair = 2,
  TwoPair = 3,
  ThreeOfAKind = 4,
  Straight = 5,
  Flush = 6,
  FullHouse = 7,
  FourOfAKind = 8,
  StraightFlush = 9,
}

/** 牌型中文名，索引即 category。索引 0 占位不用。 */
export const CATEGORY_NAMES_CN: readonly string[] = [
  '', '高牌', '一对', '两对', '三条', '顺子', '同花', '葫芦', '四条', '同花顺',
];

/** 牌型英文 key，供埋点与日志使用（中文不适合做埋点维度值）。 */
export const CATEGORY_KEYS: readonly string[] = [
  '', 'high_card', 'pair', 'two_pair', 'trips', 'straight', 'flush', 'full_house', 'quads', 'straight_flush',
];

/** 5 张牌去重后的牌力等价类总数（PRD 4.2）。既是常量也是自检基准。 */
export const EQUIVALENCE_CLASS_COUNT = 7462;

// ============================ 查表初始化 ============================

const MASK_SIZE = 1 << NUM_RANKS; // 8192

const POPC = new Uint8Array(MASK_SIZE);
const HIGH = new Int8Array(MASK_SIZE);      // 最高位 rank，空集为 -1
const TOP5 = new Int32Array(MASK_SIZE);     // 前 5 高 rank 打包成 20 位
const STRAIGHT = new Uint8Array(MASK_SIZE); // 顺子最高张 rank+1，0 表示无顺子
const FLUSHV = new Int32Array(MASK_SIZE);   // popcount>=5 时的同花/同花顺牌力值

(function initTables(): void {
  for (let m = 0; m < MASK_SIZE; m++) {
    let p = 0;
    let h = -1;
    for (let r = 0; r < NUM_RANKS; r++) {
      if (m & (1 << r)) { p++; h = r; }
    }
    POPC[m] = p;
    HIGH[m] = h;

    let packed = 0;
    let taken = 0;
    for (let r = NUM_RANKS - 1; r >= 0 && taken < 5; r--) {
      if (m & (1 << r)) { packed |= r << (16 - 4 * taken); taken++; }
    }
    TOP5[m] = packed;

    // 顺子：从 A 高往下扫；A2345（轮子）单独处理且最高张按「5」（rank 3）计，
    // 这样两个顺子直接比 high 即可，轮子天然成为最小顺子（AC1.5）。
    let sh = 0;
    for (let hi = NUM_RANKS - 1; hi >= 4; hi--) {
      const need = 0x1f << (hi - 4);
      if ((m & need) === need) { sh = hi + 1; break; }
    }
    if (sh === 0) {
      const wheel = (1 << 12) | 0xf; // A,5,4,3,2
      if ((m & wheel) === wheel) sh = 4; // high = rank 3 = 「5」
    }
    STRAIGHT[m] = sh;

    FLUSHV[m] = p >= 5
      ? (sh !== 0
        ? (HandCategory.StraightFlush << 20) | ((sh - 1) << 16)
        : (HandCategory.Flush << 20) | packed)
      : 0;
  }
})();

// ============================ 核心评估 ============================

/**
 * 由 4 个花色点数位图求牌力值。这是全引擎最热的函数（翻牌圈调用 ~107 万次），
 * 因此：形参为 4 个原始 number（不装箱、不建数组）、全程整数运算、提前返回。
 */
export function evaluateMasks(a: number, b: number, c: number, d: number): number {
  // 同花分支放最前：7 张牌里若某花色 >=5 张，则剩余 <=2 张凑不出四条或葫芦，
  // 所以「有 5 同花」⇒ 最优成手必定是同花或同花顺，可直接返回，无需再比。
  if (POPC[a] >= 5) return FLUSHV[a];
  if (POPC[b] >= 5) return FLUSHV[b];
  if (POPC[c] >= 5) return FLUSHV[c];
  if (POPC[d] >= 5) return FLUSHV[d];

  // 4 个位图按位做全加器，一次算出「每个 rank 出现几次」的二进制表示。
  // 比逐张 cnt[rank]++ 快得多，且无数组访问。
  const t = a ^ b;
  const k1 = a & b;
  const t2 = t ^ c;
  const k2 = t & c;
  const bit0 = t2 ^ d;
  const k3 = t2 & d;
  const ka = k1 ^ k2;
  const kb = k1 & k2;
  const bit1 = ka ^ k3;
  const kd = ka & k3;
  const bit2 = kb | kd;

  const m1 = bit0 | bit1 | bit2;   // 出现 >=1 次
  const m2 = bit1 | bit2;          // 出现 >=2 次
  const m3 = (bit1 & bit0) | bit2; // 出现 >=3 次
  const m4 = bit2;                 // 出现 4 次

  if (m4 !== 0) {
    const q = HIGH[m4];
    const k = HIGH[m1 & ~(1 << q)];
    return (HandCategory.FourOfAKind << 20) | (q << 16) | (k << 12);
  }

  if (m3 !== 0) {
    const tr = HIGH[m3];
    // rest 里既可能是另一组对，也可能是第二组三条（3+3+1 牌型）——
    // 两种情况都取最高者作为葫芦的「对」，语义正确。
    const rest = m2 & ~(1 << tr);
    if (rest !== 0) {
      return (HandCategory.FullHouse << 20) | (tr << 16) | (HIGH[rest] << 12);
    }
  }

  // 顺子必须在三条之前、葫芦之后判定（葫芦 7 > 顺子 5 > 三条 4）。
  const sh = STRAIGHT[m1];
  if (sh !== 0) return (HandCategory.Straight << 20) | ((sh - 1) << 16);

  if (m3 !== 0) {
    const tr = HIGH[m3];
    let rest = m1 & ~(1 << tr);
    const k1r = HIGH[rest];
    rest &= ~(1 << k1r);
    const k2r = HIGH[rest];
    return (HandCategory.ThreeOfAKind << 20) | (tr << 16) | (k1r << 12) | (k2r << 8);
  }

  if (m2 !== 0) {
    const p1 = HIGH[m2];
    const rest2 = m2 & ~(1 << p1);
    if (rest2 !== 0) {
      // 7 张可能有三组对，取最高两组；被舍弃的第三组对的点数仍是合法踢脚。
      const p2 = HIGH[rest2];
      const kk = HIGH[m1 & ~(1 << p1) & ~(1 << p2)];
      return (HandCategory.TwoPair << 20) | (p1 << 16) | (p2 << 12) | (kk << 8);
    }
    let rest = m1 & ~(1 << p1);
    const q1 = HIGH[rest]; rest &= ~(1 << q1);
    const q2 = HIGH[rest]; rest &= ~(1 << q2);
    const q3 = HIGH[rest];
    return (HandCategory.Pair << 20) | (p1 << 16) | (q1 << 12) | (q2 << 8) | (q3 << 4);
  }

  return (HandCategory.HighCard << 20) | TOP5[m1];
}

/**
 * 评估任意 5~7 张牌，返回可直接比较的牌力值（越大越强）。
 * 少于 5 张会抛错 —— 牌局中不存在「用 4 张牌比牌」的合法场景，静默返回会掩盖 bug。
 */
export function evaluate(cards: readonly Card[]): number {
  const n = cards.length;
  if (n < 5 || n > 7) {
    throw new Error(`evaluate 需要 5~7 张牌，收到 ${n} 张`);
  }
  let a = 0;
  let b = 0;
  let c = 0;
  let d = 0;
  for (let i = 0; i < n; i++) {
    const card = cards[i];
    const bit = 1 << (card >>> 2);
    switch (card & 3) {
      case 0: a |= bit; break;
      case 1: b |= bit; break;
      case 2: c |= bit; break;
      default: d |= bit; break;
    }
  }
  /*
   * 重复牌检查（AC1.10：非法参数必须被拦截）。
   *
   * 位图是「或」上去的，重复牌会被静默吞掉 —— 例如 As As Kh Qd Jc 会被算成
   * 只有 4 张有效牌的高牌，结果错误但不报错，这类静默错误极难定位。
   * 用 4 个 popcount 求和与张数比对即可检出，代价仅 4 次查表加法。
   *
   * 注意：赢率引擎的热循环走的是 evaluateMasks（自己保证牌不重复），
   * 不经过这里，所以这个检查不影响 1e6 量级枚举的性能。
   */
  if (POPC[a] + POPC[b] + POPC[c] + POPC[d] !== n) {
    throw new Error(`evaluate 收到重复的牌：${cards.join(',')}`);
  }
  return evaluateMasks(a, b, c, d);
}

/** 便捷函数：2 张底牌 + 3~5 张公共牌。 */
export function evaluateHoleAndBoard(hole: readonly Card[], board: readonly Card[]): number {
  return evaluate([...hole, ...board]);
}

export function categoryOf(value: number): HandCategory {
  return (value >>> 20) as HandCategory;
}

/** 取牌力值中第 i（0..4）个关键点数。 */
export function keyRankOf(value: number, index: number): number {
  return (value >>> (16 - 4 * index)) & 0xf;
}

// ============================ 7462 个等价类映射 ============================

let sortedValues: Int32Array | null = null;

/**
 * 构建全部 7462 个牌力等价类的有序值表（升序，下标 0 = 最弱）。
 *
 * 不枚举 C(52,5)=2,598,960 手牌（那要 ~100ms），而是直接枚举「点数多重集 + 同花位图」：
 *   - 非同花：从 13 个点数中取大小为 5、每个点数 <=4 的多重集，共 C(17,5)-13 = 6175 个
 *   - 同花：13 位位图中 popcount=5 的，共 C(13,5) = 1287 个
 *   6175 + 1287 = 7462 —— 与常量精确吻合，本身就是一次强校验。
 */
function buildEquivalenceTable(): Int32Array {
  const values = new Set<number>();

  // 同花与同花顺：单个花色的 5 位点数位图
  for (let m = 0; m < MASK_SIZE; m++) {
    if (POPC[m] === 5) values.add(FLUSHV[m]);
  }

  // 非同花：递归枚举点数多重集，构造时把同点数的副本分散到不同花色，
  // 并让全局花色轮转，保证 5 张牌不会意外凑成同花。
  const counts = new Int32Array(NUM_RANKS);
  const walk = (rank: number, remaining: number): void => {
    if (remaining === 0) {
      let a = 0, b = 0, c = 0, d = 0;
      let slot = 0;
      for (let r = 0; r < NUM_RANKS; r++) {
        for (let j = 0; j < counts[r]; j++) {
          const bit = 1 << r;
          switch (slot++ & 3) {
            case 0: a |= bit; break;
            case 1: b |= bit; break;
            case 2: c |= bit; break;
            default: d |= bit; break;
          }
        }
      }
      values.add(evaluateMasks(a, b, c, d));
      return;
    }
    if (rank >= NUM_RANKS) return;
    const max = Math.min(4, remaining);
    for (let take = 0; take <= max; take++) {
      counts[rank] = take;
      walk(rank + 1, remaining - take);
    }
    counts[rank] = 0;
  };
  walk(0, 5);

  const arr = Int32Array.from(values);
  arr.sort();
  return arr;
}

function ensureTable(): Int32Array {
  if (sortedValues === null) sortedValues = buildEquivalenceTable();
  return sortedValues;
}

/**
 * 把牌力值映射到 1..7462 的等价类序号（1 = 最弱的 7♠5♦4♣3♥2♦ 高牌，7462 = 皇家同花顺）。
 * 用于 UI 展示「本手牌在 7462 个等价类中排第 N」以及离线表/断言的稳定标识。
 */
export function equivalenceClass(value: number): number {
  const table = ensureTable();
  let lo = 0;
  let hi = table.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >>> 1;
    const v = table[mid];
    if (v === value) return mid + 1;
    if (v < value) lo = mid + 1;
    else hi = mid - 1;
  }
  throw new Error(`牌力值 ${value} 不在 7462 个等价类中，评估器实现有误`);
}

/** 等价类总数，同时用于断言表构建正确（应恒为 7462）。 */
export function equivalenceClassCount(): number {
  return ensureTable().length;
}

// ============================ 牌型描述（供 UI 文案） ============================

/** 牌型的结构化描述，UI 可自由拼装成「两对（A/9，踢脚 K）」这类文案。 */
export interface HandDescription {
  /** 原始牌力值，可直接比较。 */
  readonly value: number;
  /** 1..7462 等价类序号。 */
  readonly equivalenceClass: number;
  readonly category: HandCategory;
  /** 牌型中文名，如「两对」。 */
  readonly categoryName: string;
  /** 牌型英文 key，供埋点，如 "two_pair"。 */
  readonly categoryKey: string;
  /** 决定牌型的主要点数（降序），如两对的 [A, 9]。 */
  readonly primaryRanks: readonly number[];
  /** 踢脚牌点数（降序），如两对的 [K]。 */
  readonly kickers: readonly number[];
  /** 完整中文描述，如「两对（A/9，踢脚 K）」。 */
  readonly text: string;
}

function rankName(r: number): string {
  return RANK_NAMES_CN[r];
}

function joinRanks(ranks: readonly number[]): string {
  return ranks.map(rankName).join('/');
}

/**
 * 由牌力值生成结构化描述。
 * 各牌型的「主要点数 / 踢脚」切分口径与 PRD 1.1「同牌型依次比较关键牌与踢脚牌」一致。
 */
export function describeHandValue(value: number): HandDescription {
  const category = categoryOf(value);
  const k = (i: number): number => keyRankOf(value, i);
  let primary: number[] = [];
  let kickers: number[] = [];
  let text = '';
  const name = CATEGORY_NAMES_CN[category];

  switch (category) {
    case HandCategory.StraightFlush: {
      primary = [k(0)];
      text = k(0) === 12 ? '皇家同花顺' : `同花顺（${rankName(k(0))} 高）`;
      break;
    }
    case HandCategory.FourOfAKind: {
      primary = [k(0)];
      kickers = [k(1)];
      text = `四条（${rankName(k(0))}，踢脚 ${rankName(k(1))}）`;
      break;
    }
    case HandCategory.FullHouse: {
      primary = [k(0), k(1)];
      text = `葫芦（${rankName(k(0))} 带 ${rankName(k(1))}）`;
      break;
    }
    case HandCategory.Flush: {
      primary = [k(0)];
      kickers = [k(1), k(2), k(3), k(4)];
      text = `同花（${rankName(k(0))} 高）`;
      break;
    }
    case HandCategory.Straight: {
      primary = [k(0)];
      text = k(0) === 3 ? '顺子（5 高，A2345）' : `顺子（${rankName(k(0))} 高）`;
      break;
    }
    case HandCategory.ThreeOfAKind: {
      primary = [k(0)];
      kickers = [k(1), k(2)];
      text = `三条（${rankName(k(0))}，踢脚 ${joinRanks(kickers)}）`;
      break;
    }
    case HandCategory.TwoPair: {
      primary = [k(0), k(1)];
      kickers = [k(2)];
      text = `两对（${joinRanks(primary)}，踢脚 ${rankName(k(2))}）`;
      break;
    }
    case HandCategory.Pair: {
      primary = [k(0)];
      kickers = [k(1), k(2), k(3)];
      text = `一对（${rankName(k(0))}，踢脚 ${joinRanks(kickers)}）`;
      break;
    }
    default: {
      primary = [k(0)];
      kickers = [k(1), k(2), k(3), k(4)];
      text = `高牌（${rankName(k(0))}，踢脚 ${joinRanks(kickers)}）`;
      break;
    }
  }

  return {
    value,
    equivalenceClass: equivalenceClass(value),
    category,
    categoryName: name,
    categoryKey: CATEGORY_KEYS[category],
    primaryRanks: primary,
    kickers,
    text,
  };
}

/** 便捷函数：直接由牌描述成手。 */
export function describeHand(cards: readonly Card[]): HandDescription {
  return describeHandValue(evaluate(cards));
}

/**
 * 从 5~7 张牌中找出构成最优成手的那 5 张（供 UI「高亮成手」用，设计稿 7.7 状态）。
 * 这里可以用 21 组合暴力搜索 —— 它只在摊牌时每手调用一次，不在赢率热路径上，
 * 用最直白的实现换取可读性与绝对正确性。
 */
export function bestFiveCards(cards: readonly Card[]): Card[] {
  const n = cards.length;
  if (n < 5) throw new Error(`bestFiveCards 需要至少 5 张牌，收到 ${n} 张`);
  if (n === 5) return [...cards];
  let bestValue = -1;
  let best: Card[] = [];
  const pick: Card[] = new Array(5);
  const combine = (start: number, depth: number): void => {
    if (depth === 5) {
      const v = evaluate(pick);
      if (v > bestValue) { bestValue = v; best = [...pick]; }
      return;
    }
    for (let i = start; i < n; i++) {
      pick[depth] = cards[i];
      combine(i + 1, depth + 1);
    }
  };
  combine(0, 0);
  // 按点数降序返回，便于 UI 稳定排布
  return best.sort((x, y) => rankOf(y) - rankOf(x) || suitOf(y) - suitOf(x));
}

/** 调试辅助：把牌力值格式化为紧凑字符串。 */
export function formatHandValue(value: number): string {
  const d = describeHandValue(value);
  const ranks = [...d.primaryRanks, ...d.kickers].map((r) => RANK_LETTERS[r]).join('');
  return `${d.categoryKey}:${ranks}(#${d.equivalenceClass})`;
}
